import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, dismissAlert, type Dashboard } from "@/lib/api";
import { Button, ErrorNote, Loading, Mono, Panel } from "@/components/primitives";

/**
 * Overview (spec §12 Dashboard).
 *
 * The first thing here is a plain sentence about whether messages are moving,
 * because the failure this system has to surface is a silent one: the bot can
 * be demoted or the group deactivated and everything still *looks* fine while
 * players get no reply.
 */

function Metric({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="bg-card px-5 py-3 last:col-span-2 sm:last:col-span-1">
      <div className="text-2xl leading-tight font-semibold tracking-tight tabular-nums">
        {value.toLocaleString()}
      </div>
      <div className="text-muted-foreground">{label}</div>
      {hint && <div className="text-[12px] text-muted-foreground/80">{hint}</div>}
    </div>
  );
}

export function OverviewPage() {
  const { botId } = useParams();
  const query = useQuery({
    queryKey: ["dashboard", botId],
    queryFn: () => api.get<Dashboard>(`/bots/${botId}/dashboard`),
    refetchInterval: 60_000,
  });

  const queryClient = useQueryClient();
  const dismiss = useMutation({
    mutationFn: (type: string) => dismissAlert(botId!, type),
    // The server drops its cached dashboard too, so the refetch sees the
    // alert gone rather than the copy from up to 30 seconds ago.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dashboard", botId] }),
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorNote error={query.error} />;

  const d = query.data!;
  const undelivered = d.alerts.undeliveredPlayerMessages;
  const problems: { text: string; to?: string; dismiss?: string }[] = [
    d.alerts.hasActiveGroup
      ? null
      : { text: "No CS group is activated, so nothing can be delivered." },
    d.alerts.groupPermissionProblems > 0
      ? {
          text: "The bot is missing rights in the CS group. Players' messages cannot reach a Topic.",
        }
      : null,
    undelivered > 0
      ? {
          // These were given up on, not left retrying: the Topic already says
          // so, and the queue moved on (decision 49). Saying "still retrying"
          // would have CS waiting for something that is not coming.
          text:
            `${undelivered} player ${undelivered === 1 ? "message" : "messages"} could not be ` +
            `delivered in the last 24 hours. The ${undelivered === 1 ? "Topic says" : "Topics say"} ` +
            `so — ask the ${undelivered === 1 ? "player" : "players"} to send again if it matters.`,
          to: `/bots/${botId}/events?type=delivery.player_to_topic_delayed`,
          dismiss: "delivery.player_to_topic_delayed",
        }
      : null,
    d.messages.failed > 0
      ? { text: `${d.messages.failed} messages could not be delivered and were handed back to CS.` }
      : null,
  ].filter((x): x is { text: string; to?: string; dismiss?: string } => x !== null);

  return (
    <div className="grid grid-cols-1 gap-4">
      <section
        className={
          problems.length === 0
            ? "rounded-lg border border-ok/35 bg-ok/5 px-4 py-3.5"
            : "rounded-lg border border-destructive/45 bg-destructive/5 px-4 py-3.5"
        }
      >
        <h1 className="font-medium">
          {problems.length === 0
            ? "Messages are flowing in both directions."
            : problems.length === 1
              ? "One thing needs attention."
              : `${problems.length} things need attention.`}
        </h1>
        {problems.length > 0 ? (
          <ul className="mt-1.5 grid gap-1">
            {problems.map((problem) => (
              <li key={problem.text} className="text-destructive">
                {problem.to ? (
                  <Link to={problem.to} className="underline underline-offset-2">
                    {problem.text}
                  </Link>
                ) : (
                  problem.text
                )}
                {problem.dismiss && (
                  <Button
                    size="sm"
                    variant="quiet"
                    className="ml-2 align-baseline"
                    disabled={dismiss.isPending}
                    onClick={() => dismiss.mutate(problem.dismiss!)}
                  >
                    Dismiss
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-0.5 text-muted-foreground">
            The CS group is active and the bot has the rights it needs. Checked hourly.
          </p>
        )}
        {!d.alerts.hasActiveGroup && (
          <Link
            to={`/bots/${botId}/groups`}
            className="mt-2 inline-block font-medium text-primary underline underline-offset-2"
          >
            Choose a CS group
          </Link>
        )}
      </section>

      {/* 1px gaps over a border-coloured backing draw the rules, so the grid
          can reflow from two columns to five without any cell losing a line. */}
      <Panel className="overflow-hidden">
        <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
          <Metric label="Players" value={d.players.total} />
          <Metric label="New today" value={d.players.newToday} />
          <Metric label="Active today" value={d.players.activeToday} />
          <Metric label="Messages today" value={d.messages.today} />
          <Metric label="Active sources" value={d.sources.active} />
        </div>
      </Panel>

      <Panel title="Where players come from">
        {d.sources.top.length === 0 ? (
          <div className="px-5 py-8 text-muted-foreground">
            No players yet. Once someone opens a deep link, their source appears here.
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {d.sources.top.map((source) => {
              const share = d.players.total > 0 ? source.players / d.players.total : 0;
              return (
                <li
                  key={source.sourceId ?? "none"}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate">{source.name ?? "Unattributed"}</div>
                    {/* A bar rather than a pie: the comparison is between rows. */}
                    <div className="mt-1 h-1 rounded-full bg-muted">
                      <div
                        className="h-1 rounded-full bg-primary"
                        style={{ width: `${Math.max(share * 100, 1)}%` }}
                      />
                    </div>
                  </div>
                  <Mono className="text-right">{source.players.toLocaleString()}</Mono>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
